<template>
  <div>
    <div class="profile-field__title">Персональная информация</div>
    <div class="wrapper">
      <div v-if="field.personalData.familyStatus.show" class="form-group family-status">
        <c-select
          v-model="familyStatus"
          type="select"
          floating-label="Семейное положение"
          :items="options"
          :clearable="true"
          :custom-options="field.personalData.familyStatus.custom"
          :required-mark="field.personalData.familyStatus.required"
          :invalid="!!(error && error.familyStatus)"
          :error="error && error.familyStatus"
        />
      </div>

      <div v-if="field.personalData.hasChildren"><c-switcher v-model="hasChildren" label="Есть дети" /></div>

      <transition name="slide">
        <div v-if="hasChildren" class="children">
          <div class="children__list">
            <child
              v-for="(child, i) in children"
              :key="child.unique"
              :child="child"
              :error="error && error.children[i]"
              @remove="removeChild(child)"
            />
          </div>

          <div class="children__add">
            <c-button
              :icon="{ icon: 'plus', color: 'primary' }"
              label="Добавить ребенка"
              :disabled="preview || hasEmptyChildren"
              size="small"
              @click="addChild"
            />
          </div>
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";

import CSelect from "@/components/controls2/Select";
import CSwitcher from "@/components/controls/switcher";
import CButton from "@/components/controls2/Button";
import Child from "./Child";
import { SYSTEM_TYPES, FIELD_TYPE_PERSONAL_DATA } from "@/domains/profile/constants/system-types";

import { getUnique } from "@/utils/number/unique";
import { formatList } from "../../utils";

const fieldConfig = SYSTEM_TYPES[FIELD_TYPE_PERSONAL_DATA];

export default {
  components: { CSelect, CSwitcher, CButton, Child },
  props: ["field", "value", "preview", "editable", "error"],
  data() {
    return {
      inited: false,
      familyStatus: null,
      hasChildren: false,
      children: [],
    };
  },

  computed: {
    ...mapGetters({
      dictionaries: "profile/getDictionaries",
    }),
    dictionaryCode() {
      return fieldConfig.dictionaryCodes.familyStatuses;
    },
    variants() {
      return this.dictionaries[this.dictionaryCode] || [];
    },
    options() {
      return formatList(this.variants);
    },
    hasEmptyChildren() {
      return this.children.some((child) => !child.age);
    },
  },

  watch: {
    familyStatus() {
      this.updateValue();
    },
    hasChildren(v) {
      if (!v) {
        this.children = [];
      }
      this.updateValue();
    },

    children: {
      handler() {
        this.updateValue();
      },
      deep: true,
    },

    value(v) {
      this.setData(v);
    },
  },

  async created() {
    await this.$store.dispatch("profile/getDictionary", { code: this.dictionaryCode });
    if (this.value) this.setData(this.value);
  },

  methods: {
    setData(value) {
      if (!value) return;
      this.inited = false;
      const { familyStatus, children, hasChildren } = value.value || {};

      this.hasChildren = hasChildren === "0" ? false : !!hasChildren;

      const currentChildren = this.children.map((child) => `${child.gender}-${child.age}`);
      const newChildren = (hasChildren && children ? children : []).map((child) => `${child.gender}-${child.age}`);

      if (JSON.stringify(currentChildren) !== JSON.stringify(newChildren)) {
        this.children = (children || []).map((child) => ({ id: getUnique(), ...child }));
      }

      if (familyStatus) {
        const { id, text, custom } = familyStatus;

        if (!this.familyStatus || this.familyStatus.value !== id) {
          this.familyStatus = {
            value: `${id}`,
            label: `${text}`,
            custom,
          };
        }
      } else {
        this.familyStatus = null;
      }

      this.$nextTick(() => {
        this.inited = true;
      });
    },

    updateValue() {
      let familyStatusValue = null;

      if (this.familyStatus) {
        const { value, label, custom } = this.familyStatus;
        familyStatusValue = {
          id: value,
          text: label,
          custom,
        };
      }
      const newValue = {
        hasChildren: this.hasChildren ? 1 : 0,
        children: this.children.map((child) => ({ age: child.age, gender: child.gender })),
        familyStatus: familyStatusValue,
      };
      this.$emit("input", {
        value: newValue,
      });

      if (this.inited) {
        this.$emit("change");
      }
    },

    addChild() {
      this.children = [
        ...this.children,
        {
          unique: getUnique(),
          age: "",
          gender: "m",
        },
      ];
    },
    removeChild(child) {
      this.children = this.children.filter((c) => c !== child);
    },
  },
};
</script>

<style lang="scss" scoped>
@import "~@/assets/styles/vars";

.family-status {
  padding-top: 6px;
  width: 100%;
}
.children {
  margin-top: 25px;
}

@include bootstrapMd {
  .family-status {
    width: calc(100% / 10 * 3);
    padding-right: 15px;
  }
}

@include bootstrapLg {
  .family-status {
    width: calc(100% / 12 * 3);
  }
}
</style>
